import type { DesignerNodeData } from "../schemas/index.ts";

interface Cache {
  get: (id: number) => DesignerNodeData | undefined;
  set: (id: number, node: DesignerNodeData) => void;
}

interface Options {
  /** optional cache of processFn result */
  cache?: Cache;
}

interface Context {
  slot: { parent: { typeId: string }; name: string };
}

/** Processes all nodes in a node subtree, calling processFn and replacing that node with the result.
 * Note that it is re-entrant so the children of a processed node are drilled down rather than the unprocessed one. */
export const processNodeSubtree = (
  startNode: DesignerNodeData,
  processFn: (node: DesignerNodeData, context: Context) => DesignerNodeData,
  { cache }: Options = {},
) => {
  const processSubtree = (node: DesignerNodeData, context: Context): DesignerNodeData => {
    const cacheEntry = cache?.get(node.id);
    if (cacheEntry) return cacheEntry;
    const { placeholder, childNodes, ...mainProperties } = processFn(node, context);
    const processed = {
      ...mainProperties,
      childNodes: Object.fromEntries(
        Object.entries(childNodes).map(([name, childOrChildren]) => {
          const childContext = {
            slot: {
              parent: {
                typeId: node.typeId,
              },
              name,
            },
          };
          return [
            name,
            Array.isArray(childOrChildren)
              ? childOrChildren.map((nodeOrUndefined) =>
                  nodeOrUndefined ? processSubtree(nodeOrUndefined, childContext) : nodeOrUndefined,
                )
              : childOrChildren
                ? processSubtree(childOrChildren, childContext)
                : undefined,
          ];
        }),
      ),
      placeholder: placeholder ? processSubtree(placeholder, context) : undefined,
    };
    cache?.set(node.id, processed);
    return processed;
  };

  return processSubtree(startNode, { slot: { parent: { typeId: "" }, name: "" } });
};
